import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} tab
 * @property {HTMLElement[]} tabContent
 *
 * @extends {Component<Refs>}
 */
class NaSizeChartComponent extends Component {
    /**
     * @returns {HTMLElement[]}
     */
    get #tabs() {
        return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
    }

    /**
     * @param {MouseEvent} event
     * @param {HTMLElement} [target]
     */
    switchTab(event, target) {
        const clickedTab = this.#eventTab(event, target);

        if (!clickedTab) return;

        this.#activate(clickedTab, false);
    }

    /**
     * @param {KeyboardEvent} event
     * @param {HTMLElement} [target]
     */
    handleTabKeydown(event, target) {
        const tabs = this.#tabs;
        const currentTab = this.#eventTab(event, target);
        const currentIndex = currentTab ? tabs.indexOf(currentTab) : -1;

        if (currentIndex === -1) return;

        let nextIndex = -1;

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                nextIndex = (currentIndex + 1) % tabs.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }

        event.preventDefault();
        const nextTab = tabs[nextIndex];
        if (!nextTab) return;

        this.#activate(nextTab, true);
    }

    /**
     * @param {Event} event
     * @param {HTMLElement} [target]
     * @returns {HTMLElement | null}
     */
    #eventTab(event, target) {
        if (target?.closest) {
            const fromTarget = target.closest('[role="tab"], [data-tab]');
            if (fromTarget) return /** @type {HTMLElement} */ (fromTarget);
        }

        const eventTarget = /** @type {HTMLElement | null} */ (event?.target);
        const fromEvent = eventTarget?.closest?.('[role="tab"], [data-tab]');
        return fromEvent ? /** @type {HTMLElement} */ (fromEvent) : null;
    }

    /**
     * @param {HTMLElement} activeTab
     * @param {boolean} setFocus
     */
    #activate(activeTab, setFocus) {
        const tabId = activeTab.getAttribute('data-tab') || activeTab.getAttribute('aria-controls');
        if (!tabId) return;

        this.#tabs.forEach((tab) => {
            const isActive = tab === activeTab;
            tab.classList.toggle('active', isActive);
            tab.setAttribute('aria-selected', String(isActive));
            tab.tabIndex = isActive ? 0 : -1;
        });

        this.querySelectorAll('.tab-content').forEach((panel) => {
            panel.classList.toggle('active', panel.id === tabId);
        });

        if (setFocus) activeTab.focus();
    }
}

if (!customElements.get('na-size-chart-component')) {
    customElements.define('na-size-chart-component', NaSizeChartComponent);
}
